<script setup>
import { ref, computed } from "vue";
import SectionHeading from "../ui/SectionHeading.vue";
import BaseButton from "../ui/BaseButton.vue";
import MagneticWrap from "../ui/MagneticWrap.vue";
import IconArrow from "../icons/IconArrow.vue";
import { useLanding } from "../../composables/useLanding";
import { track } from "../../composables/useAnalytics";
import { scrollToLeadForm } from "../../composables/useScrollTo";

// The other landing page gets a chip here too, as a plain link across.
const { items: industries, crossLink } = useLanding().industries;

const activeId = ref(industries[0].id);
const active = computed(
  () => industries.find((i) => i.id === activeId.value) || industries[0]
);

function select(id) {
  if (id === activeId.value) return;
  activeId.value = id;
  track("industry_switch", { industry: id });
}
</script>

<template>
  <section class="section section--surface" id="industries">
    <div class="container">
      <SectionHeading
        eyebrow="מתאים לעסק שלכם"
        title="בחרו את סוג העסק"
        subtitle="אותה מערכת, מותאמת לאיך שהעסק שלכם באמת עובד."
      />

      <div class="switcher" role="group" aria-label="סוג העסק">
        <button
          v-for="industry in industries"
          :key="industry.id"
          class="switcher__chip"
          :class="{ 'is-active': industry.id === activeId }"
          :aria-pressed="industry.id === activeId ? 'true' : 'false'"
          @click="select(industry.id)"
        >
          {{ industry.label }}
        </button>

        <router-link
          v-if="crossLink"
          :to="crossLink.to"
          class="switcher__chip switcher__chip--link"
          @click="track('industry_cross_link', { to: crossLink.to })"
        >
          {{ crossLink.label }}
          <IconArrow aria-hidden="true" />
        </router-link>
      </div>

      <div class="industry">
        <Transition name="swap" mode="out-in">
          <div :key="active.id" class="industry__inner">
            <figure class="industry__visual">
              <img
                :src="active.image"
                :alt="active.imageAlt"
                width="320"
                height="640"
                loading="lazy"
              />
            </figure>

            <div class="industry__copy">
              <h3 class="industry__title">{{ active.headline }}</h3>
              <p class="industry__text">{{ active.text }}</p>

              <ul class="industry__tags" role="list">
                <li
                  v-for="tag in active.highlights"
                  :key="tag"
                  class="industry__tag"
                >
                  {{ tag }}
                </li>
              </ul>

              <MagneticWrap>
                <BaseButton size="lg" @click="scrollToLeadForm('industries')">
                  מתאים לי
                </BaseButton>
              </MagneticWrap>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </section>
</template>

<style scoped>
.switcher {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-2);
  margin-block-end: var(--space-7);
}

.switcher__chip {
  padding: var(--space-3) var(--space-5);
  border-radius: var(--radius-pill);
  border: 1px solid var(--line-strong);
  background: var(--surface);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink-muted);
  transition: color var(--dur-fast) var(--ease-in-out),
    border-color var(--dur-fast) var(--ease-in-out),
    background-color var(--dur-fast) var(--ease-in-out),
    transform var(--dur-fast) var(--ease-out);
}

.switcher__chip:hover {
  transform: translateY(-2px);
  border-color: var(--brand);
  color: var(--brand);
}

.switcher__chip--link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  border-style: dashed;
  text-decoration: none;
}

.switcher__chip--link :deep(svg) {
  inline-size: 16px;
  block-size: 16px;
}

.switcher__chip.is-active {
  background: var(--brand);
  border-color: var(--brand);
  color: var(--on-brand);
}

.industry__inner {
  display: grid;
  gap: var(--space-7);
  align-items: center;
}

.industry__visual {
  margin: 0;
  display: flex;
  justify-content: center;
}

.industry__visual img {
  inline-size: min(100%, 300px);
  block-size: auto;
  border-radius: var(--radius-2xl);
  box-shadow: var(--lift-3);
}

.industry__title {
  font-size: var(--text-2xl);
  margin-block-end: var(--space-3);
}

.industry__text {
  color: var(--ink-muted);
  font-size: var(--text-lg);
  max-inline-size: 48ch;
  margin-block-end: var(--space-5);
}

.industry__tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding: 0;
  margin: 0 0 var(--space-6);
  list-style: none;
}

.industry__tag {
  padding: var(--space-1) var(--space-4);
  border-radius: var(--radius-pill);
  background: var(--brand-soft);
  color: var(--brand-ink);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
}

@media (min-width: 900px) {
  .industry__inner {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  }
}
</style>
